/**
 * Export Utilities
 * Functions to export data to Excel and PDF formats
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, these are display-only formatters.
 * NO business logic or calculations.
 */

/**
 * Export data to Excel file
 */
export async function exportToExcel(
  data: any[],
  columns: { key: string; label: string }[],
  filename: string
): Promise<void> {
  // Dynamic import to avoid loading on initial page load
  const XLSX = await import('xlsx');

  // Prepare data for Excel
  const excelData = data.map((row) => {
    const excelRow: any = {};
    columns.forEach((col) => {
      excelRow[col.label] = row[col.key] ?? '';
    });
    return excelRow;
  });

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  // Write file
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

/**
 * Export data to PDF file
 */
export async function exportToPDF(
  data: any[],
  columns: { key: string; label: string }[],
  filename: string,
  title?: string
): Promise<void> {
  // Dynamic imports to avoid loading on initial page load
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc = new jsPDF();

  // Add title if provided
  if (title) {
    doc.setFontSize(16);
    doc.text(title, 14, 15);
  }

  // Prepare data for PDF table
  const tableData = data.map((row) =>
    columns.map((col) => {
      const value = row[col.key];
      if (value === null || value === undefined) {
        return '—';
      }
      if (typeof value === 'number') {
        // Format numbers appropriately
        return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
      }
      return String(value);
    })
  );

  const tableHeaders = columns.map((col) => col.label);

  // Add table
  autoTable(doc, {
    head: [tableHeaders],
    body: tableData,
    startY: title ? 22 : 15,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [4, 55, 123], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: title ? 22 : 15 },
  });

  // Save PDF
  doc.save(`${filename}.pdf`);
}

